import { useState } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import { scrollToId } from "../lib/scroll";
import { Reveal, SectionHead } from "./Reveal";

const API = "/api";
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const INTEREST_FOR = ["Myself", "A family member", "My organization or clients"];
const ORG_TYPES = ["Senior living", "PT or OT clinic", "Community or recreation center", "Faith community", "Fall-prevention coalition", "Other"];

const Field = ({ label, required = false, optional = false, error, htmlFor, children }) => (
    <div>
        <label htmlFor={htmlFor} className="field-label">
            {label}
            {required && <span aria-hidden="true"> *</span>}
            {optional && <span className="font-normal text-ink-soft/80"> (optional)</span>}
        </label>
        {children}
        {error && (
            <p className="error-text" role="alert" data-testid={`${htmlFor}-error`}>
                {error}
            </p>
        )}
    </div>
);

function InterestForm() {
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm({ defaultValues: { interest_for: "Myself" } });
    const [status, setStatus] = useState("idle");

    const onSubmit = async (data) => {
        try {
            await axios.post(`${API}/interest-signups`, data);
            reset();
            setStatus("success");
        } catch {
            setStatus("error");
        }
    };

    if (status === "success") {
        return (
            <div role="status" data-testid="interest-success" className="flex h-full flex-col items-start justify-center gap-4 rounded-3xl border border-teal-600/30 bg-teal-50 p-8">
                <CheckCircle2 className="h-10 w-10 text-teal-700" aria-hidden="true" />
                <p className="font-display text-2xl font-extrabold text-ink">
                    Thank you! You&rsquo;ll be among the first to hear about workshop dates.
                </p>
                <button type="button" onClick={() => setStatus("idle")} className="inline-flex items-center rounded-full px-1 py-2 text-base font-bold text-teal-700 underline" data-testid="interest-reset">
                    Add another person
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} noValidate data-testid="interest-form" className="h-full rounded-3xl border border-clay bg-white p-7 sm:p-9">
            <h3 className="font-display text-2xl font-extrabold text-ink">Be the first to know</h3>
            <div className="mt-6 space-y-6">
                <Field label="First name" required error={errors.first_name?.message} htmlFor="interest-first-name">
                    <input
                        id="interest-first-name"
                        data-testid="interest-first-name-input"
                        className="input-field"
                        autoComplete="given-name"
                        {...register("first_name", {
                            required: "Please enter your first name.",
                            maxLength: { value: 100, message: "Please keep it under 100 characters." },
                        })}
                    />
                </Field>
                <Field label="Email" required error={errors.email?.message} htmlFor="interest-email">
                    <input
                        id="interest-email"
                        type="email"
                        data-testid="interest-email-input"
                        className="input-field"
                        autoComplete="email"
                        {...register("email", {
                            required: "Please enter your email address.",
                            pattern: { value: EMAIL_RE, message: "That email address doesn't look right." },
                        })}
                    />
                </Field>
                <Field label="ZIP code" optional error={errors.zip_code?.message} htmlFor="interest-zip">
                    <input
                        id="interest-zip"
                        data-testid="interest-zip-input"
                        className="input-field"
                        inputMode="numeric"
                        autoComplete="postal-code"
                        {...register("zip_code", {
                            maxLength: { value: 10, message: "ZIP code looks too long." },
                        })}
                    />
                </Field>
                <fieldset>
                    <legend className="field-label">I&rsquo;m interested for</legend>
                    <div className="space-y-3">
                        {INTEREST_FOR.map((opt) => (
                            <label key={opt} className="flex items-center gap-3 py-2.5 text-lg text-ink">
                                <input
                                    type="radio"
                                    value={opt}
                                    data-testid={`interest-for-${opt.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className="h-6 w-6 accent-teal-700"
                                    {...register("interest_for")}
                                />
                                {opt}
                            </label>
                        ))}
                    </div>
                </fieldset>
                <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                    data-testid="interest-honeypot"
                    {...register("company")}
                />
                {status === "error" && (
                    <p className="error-text" role="alert" data-testid="interest-submit-error">
                        Something went wrong sending your request. Please try again.
                    </p>
                )}
                <button type="submit" disabled={isSubmitting} data-testid="interest-submit-button" className="btn-primary w-full text-lg">
                    {isSubmitting ? "Sending…" : "Keep me posted"}
                </button>
            </div>
        </form>
    );
}

function PartnerForm() {
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm();
    const [status, setStatus] = useState("idle");

    const onSubmit = async (data) => {
        try {
            await axios.post(`${API}/partner-inquiries`, data);
            reset();
            setStatus("success");
        } catch {
            setStatus("error");
        }
    };

    if (status === "success") {
        return (
            <div role="status" data-testid="partner-success" className="flex h-full flex-col items-start justify-center gap-4 rounded-3xl border border-teal-600/30 bg-teal-50 p-8">
                <CheckCircle2 className="h-10 w-10 text-teal-700" aria-hidden="true" />
                <p className="font-display text-2xl font-extrabold text-ink">
                    Thank you! Sarah will reply within two business days.
                </p>
                <button type="button" onClick={() => setStatus("idle")} className="inline-flex items-center rounded-full px-1 py-2 text-base font-bold text-teal-700 underline" data-testid="partner-reset">
                    Send another inquiry
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} noValidate data-testid="partner-form" className="h-full rounded-3xl border border-clay bg-white p-7 sm:p-9">
            <h3 className="font-display text-2xl font-extrabold text-ink">Bring Fall Safe Ready to your organization</h3>
            <div className="mt-6 space-y-6">
                <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Name" required error={errors.name?.message} htmlFor="partner-name">
                        <input
                            id="partner-name"
                            data-testid="partner-name-input"
                            className="input-field"
                            autoComplete="name"
                            {...register("name", { required: "Please enter your name." })}
                        />
                    </Field>
                    <Field label="Organization" required error={errors.organization?.message} htmlFor="partner-organization">
                        <input
                            id="partner-organization"
                            data-testid="partner-organization-input"
                            className="input-field"
                            autoComplete="organization"
                            {...register("organization", { required: "Please enter your organization." })}
                        />
                    </Field>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Role" optional error={errors.role?.message} htmlFor="partner-role">
                        <input id="partner-role" data-testid="partner-role-input" className="input-field" {...register("role")} />
                    </Field>
                    <Field label="Email" required error={errors.email?.message} htmlFor="partner-email">
                        <input
                            id="partner-email"
                            type="email"
                            data-testid="partner-email-input"
                            className="input-field"
                            autoComplete="email"
                            {...register("email", {
                                required: "Please enter your email address.",
                                pattern: { value: EMAIL_RE, message: "That email address doesn't look right." },
                            })}
                        />
                    </Field>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Phone" optional error={errors.phone?.message} htmlFor="partner-phone">
                        <input
                            id="partner-phone"
                            type="tel"
                            data-testid="partner-phone-input"
                            className="input-field"
                            autoComplete="tel"
                            {...register("phone")}
                        />
                    </Field>
                    <Field label="Organization type" optional error={errors.org_type?.message} htmlFor="partner-org-type">
                        <select id="partner-org-type" data-testid="partner-org-type-select" className="input-field" defaultValue="" {...register("org_type")}>
                            <option value="">Select an option</option>
                            {ORG_TYPES.map((t) => (
                                <option key={t} value={t}>
                                    {t}
                                </option>
                            ))}
                        </select>
                    </Field>
                </div>
                <Field label="Message" optional error={errors.message?.message} htmlFor="partner-message">
                    <textarea
                        id="partner-message"
                        data-testid="partner-message-input"
                        className="textarea-field"
                        {...register("message", { maxLength: { value: 4000, message: "Please keep it under 4000 characters." } })}
                    />
                </Field>
                <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                    data-testid="partner-honeypot"
                    {...register("website")}
                />
                {status === "error" && (
                    <p className="error-text" role="alert" data-testid="partner-submit-error">
                        Something went wrong sending your inquiry. Please try again.
                    </p>
                )}
                <button type="submit" disabled={isSubmitting} data-testid="partner-submit-button" className="btn-primary w-full text-lg">
                    {isSubmitting ? "Sending…" : "Send inquiry"}
                </button>
            </div>
        </form>
    );
}

export function QuickInterestForm() {
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm({ defaultValues: { interest_for: "Myself" } });
    const [status, setStatus] = useState("idle");

    const onSubmit = async (data) => {
        try {
            await axios.post(`${API}/interest-signups`, data);
            reset();
            setStatus("success");
        } catch {
            setStatus("error");
        }
    };

    if (status === "success") {
        return (
            <section id="join" className="scroll-mt-28 border-b border-clay bg-sand/60 py-10 sm:py-14">
                <div className="mx-auto max-w-3xl px-5 sm:px-8">
                    <div role="status" data-testid="quick-interest-success" className="flex flex-col items-start gap-3 rounded-3xl border border-teal-600/30 bg-teal-50 p-6 sm:p-8">
                        <CheckCircle2 className="h-10 w-10 text-teal-700" aria-hidden="true" />
                        <p className="font-display text-2xl font-extrabold text-ink">
                            Thank you! You&rsquo;ll be among the first to hear about workshop dates.
                        </p>
                        <button type="button" onClick={() => setStatus("idle")} className="inline-flex items-center rounded-full px-1 py-2 text-base font-bold text-teal-700 underline" data-testid="quick-interest-reset">
                            Add another person
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section id="join" className="scroll-mt-28 border-b border-clay bg-sand/60 py-10 sm:py-14">
            <div className="mx-auto max-w-3xl px-5 sm:px-8">
                <form onSubmit={handleSubmit(onSubmit)} noValidate data-testid="quick-interest-form" className="rounded-3xl border border-clay bg-white p-6 sm:p-8">
                    <h3 className="font-display text-2xl font-extrabold text-ink">Save your spot — it takes 30 seconds</h3>
                    <p className="mt-2 text-base text-ink-soft">Be first in line for early-2027 workshop dates in Arlington.</p>
                    <div className="mt-5 grid gap-5 sm:grid-cols-2">
                        <Field label="First name" required error={errors.first_name?.message} htmlFor="quick-interest-first-name">
                            <input
                                id="quick-interest-first-name"
                                data-testid="quick-interest-first-name-input"
                                className="input-field"
                                autoComplete="given-name"
                                {...register("first_name", {
                                    required: "Please enter your first name.",
                                    maxLength: { value: 100, message: "Please keep it under 100 characters." },
                                })}
                            />
                        </Field>
                        <Field label="Email" required error={errors.email?.message} htmlFor="quick-interest-email">
                            <input
                                id="quick-interest-email"
                                data-testid="quick-interest-email-input"
                                type="email"
                                className="input-field"
                                autoComplete="email"
                                {...register("email", {
                                    required: "Please enter your email address.",
                                    pattern: { value: EMAIL_RE, message: "That email address doesn't look right." },
                                })}
                            />
                        </Field>
                    </div>
                    <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        className="hidden"
                        data-testid="quick-interest-honeypot"
                        {...register("company")}
                    />
                    {status === "error" && (
                        <p className="error-text mt-4" role="alert" data-testid="quick-interest-submit-error">
                            Something went wrong sending your request. Please try again.
                        </p>
                    )}
                    <button type="submit" disabled={isSubmitting} data-testid="quick-interest-submit-button" className="btn-primary mt-5 w-full text-lg">
                        {isSubmitting ? "Sending…" : "Keep me posted"}
                    </button>
                    <p className="mt-4 text-base text-ink-soft">
                        Prefer to look around first?{" "}
                        <a
                            href="#what-youll-learn"
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToId("what-youll-learn");
                            }}
                            className="font-bold text-teal-700 underline"
                            data-testid="quick-learn-link"
                        >
                            See what you&rsquo;ll learn
                        </a>{" "}
                        — the full form is at the bottom of the page.
                    </p>
                </form>
            </div>
        </section>
    );
}

export default function SignupForms() {
    return (
        <section id="signup" className="scroll-mt-24 bg-ink py-12 sm:py-16 lg:py-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal>
                    <SectionHead eyebrow="Get started" title="Join the interest list" dark />
                </Reveal>
                <div className="mt-8 grid gap-8 sm:mt-12 lg:grid-cols-2">
                    <Reveal delay={0.05} className="h-full">
                        <InterestForm />
                    </Reveal>
                    <Reveal delay={0.15} className="h-full">
                        <PartnerForm />
                    </Reveal>
                </div>
            </div>
        </section>
    );
}